/**
 * Hideout building generator (`hush.hideout_building.v1`) — a small lived-in structure that a scene recipe can
 * half-hide with growth and clutter (hideout plan 02/05). One parametric builder, four variants:
 *   boathouse     — plank walls on a stone footing, sagging single-pitch reed thatch, stovepipe (Drowned Road)
 *   woodcutter_hut — log walls, shingle gable, stone chimney (forest)
 *   tollhouse     — stone walls, slate gable, arched door (moor)
 *   boat_shelter  — an upturned hull on short plank walls (beach)
 * Every variant has a door opening lit warm from inside, a shutter or door that leaks light, a door-eave hook for
 * the lantern, and roof-edge hook points for overgrowth. Hook order (catalog `hookPoints`): eave, roof edges 0..3.
 * Decoration only: the building is not standable and generates no collision.
 */
import { doorOpening, footing, gable, hull, logs, planks, shedThatch, shutteredWindow, stone, stoneChimney, stovepipe, type Rect, type RoofArt } from './buildingParts';
import { checkEnvelope, checkScope, el, n, partGroup, rngFor, type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette } from '../pieces/svg';

export type BuildingVariant = 'boathouse' | 'woodcutter_hut' | 'tollhouse' | 'boat_shelter';
export type BuildingRecipe = PieceRecipe<'hush.hideout_building.v1', { variant: BuildingVariant; wear: number; roofSag: number }>;

interface Layout {
  frame: { w: number; h: number };
  foot?: Rect;
  wall: Rect;
  walls: 'planks' | 'logs' | 'stone';
  roof: (roles: ResolvedPalette, rng: (c: string) => number, sag: number) => RoofArt & { topAt: (x: number) => number };
  door: Rect;
  arched: boolean;
  window?: Rect;
  eaveHook: Point;
  chimney?: { kind: 'pipe' | 'stone'; x: number; topY: number };
}

const quad = (a: Point, m: Point, b: Point) => (x: number) => {
  const t = (x - a.x) / (b.x - a.x), u = 1 - t;
  return u * u * a.y + 2 * u * t * m.y + t * t * b.y;
};

export const BUILDING_LAYOUTS: Readonly<Record<BuildingVariant, Layout>> = {
  boathouse: {
    frame: { w: 260, h: 210 }, foot: { x: 26, y: 192, w: 208, h: 18 }, wall: { x: 32, y: 40, w: 196, h: 152 }, walls: 'planks',
    roof: (roles, rng, sag) => {
      const l = { x: 10, y: 34 }, r = { x: 250, y: 74 };
      const art = shedThatch(roles, l, r, 24, sag, rng);
      const m = { x: 130, y: 54 + sag };
      return { ...art, topAt: (x: number) => quad(l, m, r)(x) + 12 };
    },
    door: { x: 148, y: 104, w: 48, h: 88 }, arched: false, window: { x: 58, y: 118, w: 40, h: 30 },
    eaveHook: { x: 214, y: 104 }, chimney: { kind: 'pipe', x: 82, topY: 8 },
  },
  woodcutter_hut: {
    frame: { w: 240, h: 210 }, foot: { x: 28, y: 194, w: 184, h: 16 }, wall: { x: 36, y: 60, w: 168, h: 134 }, walls: 'logs',
    roof: (roles) => ({ ...gable(roles, 14, 226, 108, 34, false), topAt: (x: number) => 34 + 74 * Math.abs(x - 120) / 106 + 6 }),
    door: { x: 96, y: 114, w: 44, h: 80 }, arched: false, window: { x: 48, y: 130, w: 34, h: 28 },
    eaveHook: { x: 154, y: 118 }, chimney: { kind: 'stone', x: 176, topY: 22 },
  },
  tollhouse: {
    frame: { w: 230, h: 220 }, foot: { x: 24, y: 206, w: 182, h: 13.4 }, wall: { x: 30, y: 60, w: 170, h: 146 }, walls: 'stone',
    roof: (roles) => ({ ...gable(roles, 16, 214, 100, 30, true), topAt: (x: number) => 30 + 70 * Math.abs(x - 115) / 99 + 6 }),
    door: { x: 92, y: 120, w: 44, h: 86 }, arched: true, window: { x: 44, y: 128, w: 34, h: 28 },
    eaveHook: { x: 150, y: 124 }, chimney: { kind: 'stone', x: 62, topY: 18 },
  },
  boat_shelter: {
    frame: { w: 260, h: 170 }, foot: { x: 22, y: 156, w: 216, h: 14 }, wall: { x: 30, y: 70, w: 200, h: 86 }, walls: 'planks',
    roof: (roles) => ({ ...hull(roles, 10, 250, 92, 84), topAt: () => 92 }),
    door: { x: 112, y: 96, w: 40, h: 60 }, arched: false,
    eaveHook: { x: 166, y: 98 },
  },
};

export const BUILDING_FRAMES = Object.fromEntries(Object.entries(BUILDING_LAYOUTS).map(([k, v]) => [k, v.frame])) as Record<BuildingVariant, { w: number; h: number }>;

export function validateBuildingRecipe(input: unknown): BuildingRecipe {
  checkEnvelope(input, 'hush.hideout_building.v1');
  const r = input as BuildingRecipe;
  if (!Object.prototype.hasOwnProperty.call(BUILDING_LAYOUTS, r.params.variant)) throw new Error('hush.hideout_building.v1: variant');
  for (const k of ['wear', 'roofSag'] as const) {
    const v = r.params[k];
    if (typeof v !== 'number' || !(v >= 0 && v <= 1)) throw new Error(`hush.hideout_building.v1: ${k} must be 0..1`);
  }
  return r;
}

export function buildHideoutBuilding(input: BuildingRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  const recipe = validateBuildingRecipe(input);
  checkScope(scope);
  const L = BUILDING_LAYOUTS[recipe.params.variant];
  const rng = rngFor(recipe);
  const parts: string[] = [];
  const roof = L.roof(roles, rng, 4 + recipe.params.roofSag * 12);

  // Walls are clipped under the roof line so no wall shows above the eaves.
  const clipId = `${scope}_wallclip`;
  const steps = 24;
  const wallTop = Array.from({ length: steps + 1 }, (_, i) => {
    const x = L.wall.x + (L.wall.w * i) / steps;
    return `L${n(x)} ${n(Math.max(L.wall.y, roof.topAt(x)))}`;
  }).join('');
  const clip = el('clipPath', { id: clipId }, el('path', { d: `M${n(L.wall.x - 6)} ${n(L.wall.y + L.wall.h)}${wallTop}L${n(L.wall.x + L.wall.w + 6)} ${n(L.wall.y + L.wall.h)}Z` }));
  const door = doorOpening(roles, L.door, L.arched, scope);
  const defs = el('defs', {}, clip + door.defs);

  const wallArt = L.walls === 'planks' ? planks(roles, L.wall, rng, recipe.params.wear)
    : L.walls === 'logs' ? logs(roles, L.wall, rng) : stone(roles, L.wall, rng);
  let body = defs;
  if (L.chimney) {
    const base = roof.topAt(L.chimney.x) + 8;
    body += partGroup(parts, 'building-chimney', L.chimney.kind === 'pipe'
      ? stovepipe(roles, L.chimney.x, base, L.chimney.topY) : stoneChimney(roles, L.chimney.x, base, L.chimney.topY));
  }
  body += partGroup(parts, 'building-walls', wallArt, { 'clip-path': `url(#${clipId})` });
  if (L.foot) body += partGroup(parts, 'building-footing', footing(roles, L.foot, rng));
  body += partGroup(parts, 'building-door', door.markup);
  if (L.window) body += partGroup(parts, 'building-window', shutteredWindow(roles, L.window));
  body += partGroup(parts, 'building-roof', roof.markup);
  // The lantern bracket: a short iron arm off the wall under the eave, ending in the hook point.
  const h = L.eaveHook;
  body += partGroup(parts, 'building-eave-bracket',
    el('path', { d: `M${n(h.x - 12)} ${n(h.y - 4)}H${n(h.x)}V${n(h.y)}`, stroke: roles.outline, 'stroke-width': 2.2, fill: 'none' })
    + el('circle', { cx: h.x - 12, cy: h.y - 4, r: 2, fill: roles.outline }));

  const { w, h: fh } = L.frame;
  const doorCenter = { x: L.door.x + L.door.w / 2, y: L.door.y + L.door.h };
  const anchors: Record<string, Point> = {
    support: { x: w / 2, y: fh },
    door: doorCenter,
    door_top: { x: doorCenter.x, y: L.door.y },
    door_eave_hook: h,
    light_leak_0: { x: doorCenter.x, y: L.door.y + L.door.h * 0.7 },
  };
  roof.edge.forEach((p, i) => { anchors[`roof_edge_${i}`] = p; });
  if (L.window) {
    anchors.window_0 = { x: L.window.x + L.window.w / 2, y: L.window.y + L.window.h / 2 };
    anchors.light_leak_1 = anchors.window_0;
  }
  if (L.chimney) anchors.stovepipe = { x: L.chimney.x, y: L.chimney.topY };
  return { frame: { w, h: fh }, body, anchors, parts };
}

/** Catalog hook points in order: door-eave hook, then roof edges 0..3. */
export function buildingHookPoints(art: HideoutPieceArt): Point[] {
  return [art.anchors.door_eave_hook, ...[0, 1, 2, 3].map(i => art.anchors[`roof_edge_${i}`])];
}
